import dayjs, { Dayjs } from 'dayjs';
import { BonusRule } from 'repository/timekeeper/@types/BonusRuleModel';
import { TimekeeperService } from 'services/TimekeeperService';

const tk = new TimekeeperService();

/* interface RowData {
    id: number;
    name: string;
    amount: string;
    paycode: string;
    break_rule: string;
    activate_by: string;
    description: string;
} */

const gettingBonusAndDeduction = (setLoading: (loading: boolean) => void, setRows: (rows: BonusRule[]) => void): void => {
    setLoading(true);

    tk.bonus_rules
        .get()
        .then((response) => {
            setRows(response.map((row) => ({ ...row })));
        })
        .catch((error: Error) => {
            console.error(error.message);
        })
        .finally(() => {
            setLoading(false);
        });
};

const gettingBonusAndDeductionById = (
    setLoading: (loading: boolean) => void,
    id: number,
    setName: (name: string) => void,
    setDescription: (description: string) => void,
    setAmount: (amount: string) => void,
    setMonday: (checked: boolean) => void,
    setTuesday: (checked: boolean) => void,
    setWednesday: (checked: boolean) => void,
    setThursday: (checked: boolean) => void,
    setFriday: (checked: boolean) => void,
    setSaturday: (checked: boolean) => void,
    setSunday: (checked: boolean) => void,
    setPaycode: (id: number | string) => void,
    setBreak: (id: number | string) => void,
    setAllowCancel: (checked: boolean) => void,
    setIsException: (checked: boolean) => void,
    setShiftRestriction: (checked: boolean) => void,
    setStart: (date: Dayjs | null) => void,
    setEnd: (date: Dayjs | null) => void,
    setActivateBy: (value: string) => void,
    setHours: (date: Dayjs | null) => void,
    setUseHourLimit: (checked: boolean) => void,
    setBreakException: (checked: boolean) => void,
    setMinimumInterval: (value: string) => void,
    setMinimumDuration: (value: string) => void,
    setMaximumDuration: (value: string) => void,
    setLocator: (value: string) => void,
    setLater: (date: Dayjs | null) => void,
    setEarlier: (date: Dayjs | null) => void,
    setPaycode2: (id: number | string) => void,
    setMinimum: (value: string) => void,
    setMaximum: (value: string) => void,
    setPeriod: (value: string) => void,
    setWeek: (value: string) => void,
    setFulfillRequirement: (checked: boolean) => void,
    setRequiredShift: (value: number) => void,
    setBonus: (bonus: any) => void,
): void => {
    setLoading(true);

    tk.bonus_rules
        .getById(id)
        .then((response: any) => {
            setName(response.name);
            setDescription(response.description ?? '');
            setAmount(response.amount);
            setMonday(response.days_in_week.includes('monday'));
            setTuesday(response.days_in_week.includes('tuesday'));
            setWednesday(response.days_in_week.includes('wednesday'));
            setThursday(response.days_in_week.includes('thursday'));
            setFriday(response.days_in_week.includes('friday'));
            setSaturday(response.days_in_week.includes('saturday'));
            setSunday(response.days_in_week.includes('sunday'));
            setPaycode(response.paycode_id);
            setBreak(response.break_id);
            setAllowCancel(response.allow_cancel_in_timesheet);
            setIsException(response.is_exception);
            setShiftRestriction(response.use_shift_restriction);

            if (response.start_shift_restriction) {
                setStart(dayjs(response.start_shift_restriction, 'HH:mm:ss'));
            }

            if (response.end_shift_restriction) {
                setEnd(dayjs(response.end_shift_restriction, 'HH:mm:ss'));
            }

            setActivateBy(response.activate_by);

            if (response.activate_by === 'hour_day' && response.hour) {
                setHours(dayjs(response.hour, 'HH:mm:ss'));
            }

            if (response.activate_by === 'shift_duration') {
                setUseHourLimit(response.use_limit_hour_shift_round ?? true);
                setBreakException(response.exception_short_break_unqualif_break ?? false);
                setMinimumInterval(response.min_interval_qualif ?? '00:00');
                setMinimumDuration(response.min_duration_shift_to_activate ?? '00:00');
                setMaximumDuration(response.max_duration_shift_to_activate ?? '99:59');
                setLocator(response.location ?? '04:00');
                if (response.activate_if_location_match_or_after) {
                    setLater(dayjs(response.activate_if_location_match_or_after, 'HH:mm:ss'));
                }
                if (response.activate_if_location_is_befora) {
                    setEarlier(dayjs(response.activate_if_location_is_befora, 'HH:mm:ss'));
                }
            }

            if (response.activate_by === 'paycode') {
                setPaycode2(response.active_paycode_id ?? '');
                setMinimum(response.minimum ?? '00:00');
                setMaximum(response.maximum ?? '999:59');
                setPeriod(response.time_period ?? 'shift');
                setWeek(response.start_day_week ?? 'monday');
                setFulfillRequirement(response.achieve_requirements_for_shift ?? false);
                setRequiredShift(response.shifts_required ?? 1);
            }

            setBonus(response);
        })
        .catch((error: Error) => {
            console.error(error.message);
        })
        .finally(() => {
            setLoading(false);
        });
};

const addingBonusAndDeductionHourDay = (
    name: string,
    description: string,
    amount: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean,
    is_exception: boolean,
    paycode_id: number | string,
    break_id: number | string,
    allow_cancel_in_timesheet: boolean,
    use_shift_restriction: boolean,
    start_shift_restriction: Dayjs | null,
    end_shift_restriction: Dayjs | null,
    activate_by: string,
    hour: Dayjs | null,
) => {
    const days_in_week = [
        monday ? 1 : undefined,
        tuesday ? 2 : undefined,
        wednesday ? 4 : undefined,
        thursday ? 8 : undefined,
        friday ? 16 : undefined,
        saturday ? 32 : undefined,
        sunday ? 64 : undefined,
    ]
        .map((day) => day)
        .filter((day) => day !== undefined);

    const body = {
        name,
        description,
        amount: amount,
        days_in_week,
        is_exception,
        paycode_id: paycode_id ? paycode_id : undefined,
        break_id: break_id ? break_id : undefined,
        allow_cancel_in_timesheet,
        use_shift_restriction,
        start_shift_restriction: use_shift_restriction && start_shift_restriction ? start_shift_restriction.format('HH:mm') : undefined,
        end_shift_restriction: use_shift_restriction && end_shift_restriction ? end_shift_restriction.format('HH:mm') : undefined,
        activate_by,
        hour: hour ? hour.format('HH:mm') : undefined,
    };
    console.log(`Body to create bonus/deduction with hour/day:`, body);
    return tk.bonus_rules.create(body);
};

const addingBonusAndDeductionShiftDuration = (
    name: string,
    description: string,
    amount: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean,
    is_exception: boolean,
    paycode_id: number | string,
    break_id: number | string,
    allow_cancel_in_timesheet: boolean,
    use_shift_restriction: boolean,
    start_shift_restriction: Dayjs | null,
    end_shift_restriction: Dayjs | null,
    activate_by: string,
    use_limit_hour_shift_round: boolean,
    exception_short_break_unqualif_break: boolean,
    min_interval_qualif: string,
    min_duration_shift_to_activate: string,
    max_duration_shift_to_activate: string,
    location: string,
    activate_if_location_match_or_after: Dayjs | null,
    activate_if_location_is_befora: Dayjs | null,
) => {
    const days_in_week = [
        monday ? 1 : undefined,
        tuesday ? 2 : undefined,
        wednesday ? 4 : undefined,
        thursday ? 8 : undefined,
        friday ? 16 : undefined,
        saturday ? 32 : undefined,
        sunday ? 64 : undefined,
    ]
        .map((day) => day)
        .filter((day) => day !== undefined);

    const body = {
        name,
        description,
        amount: amount,
        days_in_week,
        is_exception,
        paycode_id: paycode_id ? paycode_id : undefined,
        break_id: break_id ? break_id : undefined,
        allow_cancel_in_timesheet,
        use_shift_restriction,
        start_shift_restriction: use_shift_restriction && start_shift_restriction ? start_shift_restriction.format('HH:mm') : undefined,
        end_shift_restriction: use_shift_restriction && end_shift_restriction ? end_shift_restriction.format('HH:mm') : undefined,
        activate_by,
        use_limit_hour_shift_round,
        exception_short_break_unqualif_break,
        min_interval_qualif: min_interval_qualif,
        min_duration_shift_to_activate: min_duration_shift_to_activate,
        max_duration_shift_to_activate: max_duration_shift_to_activate,
        location: location,
        activate_if_location_match_or_after: activate_if_location_match_or_after ? activate_if_location_match_or_after.format('HH:mm') : undefined,
        activate_if_location_is_befora: activate_if_location_is_befora ? activate_if_location_is_befora.format('HH:mm') : undefined,
    };
    console.log(`Body to create bonus/deduction with shift duration:`, body);
    return tk.bonus_rules.create(body);
};

const addingBonusAndDeductionPaycode = (
    name: string,
    description: string,
    amount: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean,
    is_exception: boolean,
    paycode_id: number | string,
    break_id: number | string,
    allow_cancel_in_timesheet: boolean,
    use_shift_restriction: boolean,
    start_shift_restriction: Dayjs | null,
    end_shift_restriction: Dayjs | null,
    activate_by: string,
    active_paycode_id: number | string,
    minimum: string,
    maximum: string,
    time_period: string,
    achieve_requirements_for_shift: boolean,
    shifts_required: number,
    start_day_week: string,
) => {
    const days_in_week = [
        monday ? 1 : undefined,
        tuesday ? 2 : undefined,
        wednesday ? 4 : undefined,
        thursday ? 8 : undefined,
        friday ? 16 : undefined,
        saturday ? 32 : undefined,
        sunday ? 64 : undefined,
    ]
        .map((day) => day)
        .filter((day) => day !== undefined);

    const body = {
        name,
        description,
        amount: amount,
        days_in_week,
        is_exception,
        paycode_id: paycode_id,
        break_id: break_id,
        allow_cancel_in_timesheet,
        use_shift_restriction,
        start_shift_restriction: start_shift_restriction ? start_shift_restriction.format('HH:mm') : undefined,
        end_shift_restriction: end_shift_restriction ? end_shift_restriction.format('HH:mm') : undefined,
        activate_by,
        active_paycode_id: active_paycode_id,
        minimum: minimum,
        maximum: maximum,
        time_period,
        achieve_requirements_for_shift: achieve_requirements_for_shift,
        shifts_required: shifts_required,
        start_day_week: start_day_week,
    };

    console.log(`Body to create bonus/deduction with paycode:`, body);

    return tk.bonus_rules.create(body);
};

const editingBonusAndDeductionHourDay = (
    bonus_id: number,
    name: string,
    description: string,
    amount: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean,
    is_exception: boolean,
    paycode_id: number | string,
    break_id: number | string,
    allow_cancel_in_timesheet: boolean,
    use_shift_restriction: boolean,
    start_shift_restriction: Dayjs | null,
    end_shift_restriction: Dayjs | null,
    activate_by: string,
    hour: Dayjs | null,
) => {
    const days_in_week = [
        monday ? 1 : undefined,
        tuesday ? 2 : undefined,
        wednesday ? 4 : undefined,
        thursday ? 8 : undefined,
        friday ? 16 : undefined,
        saturday ? 32 : undefined,
        sunday ? 64 : undefined,
    ]
        .map((day) => day)
        .filter((day) => day !== undefined);

    const body = {
        name,
        description,
        amount: amount,
        days_in_week,
        is_exception,
        paycode_id: paycode_id,
        break_id: break_id,
        allow_cancel_in_timesheet,
        use_shift_restriction,
        start_shift_restriction: start_shift_restriction ? start_shift_restriction.format('HH:mm') : undefined,
        end_shift_restriction: end_shift_restriction ? end_shift_restriction.format('HH:mm') : undefined,
        activate_by,
        hour: hour ? hour.format('HH:mm') : undefined,
    };

    console.log(`Body to edit bonus/deduction with hour/day:`, body);

    return tk.bonus_rules.update(bonus_id, body);
};

const editingBonusAndDeductionShiftDuration = (
    bonus_id: number,
    name: string,
    description: string,
    amount: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean,
    is_exception: boolean,
    paycode_id: number | string,
    break_id: number | string,
    allow_cancel_in_timesheet: boolean,
    use_shift_restriction: boolean,
    start_shift_restriction: Dayjs | null,
    end_shift_restriction: Dayjs | null,
    activate_by: string,
    use_limit_hour_shift_round: boolean,
    exception_short_break_unqualif_break: boolean,
    min_interval_qualif: string,
    min_duration_shift_to_activate: string,
    max_duration_shift_to_activate: string,
    location: string,
    activate_if_location_match_or_after: Dayjs | null,
    activate_if_location_is_befora: Dayjs | null,
) => {
    const days_in_week = [
        monday ? 1 : undefined,
        tuesday ? 2 : undefined,
        wednesday ? 4 : undefined,
        thursday ? 8 : undefined,
        friday ? 16 : undefined,
        saturday ? 32 : undefined,
        sunday ? 64 : undefined,
    ]
        .map((day) => day)
        .filter((day) => day !== undefined);

    const body = {
        name,
        description,
        amount: amount,
        days_in_week,
        is_exception,
        paycode_id: paycode_id,
        break_id: break_id,
        allow_cancel_in_timesheet,
        use_shift_restriction,
        start_shift_restriction: start_shift_restriction ? start_shift_restriction.format('HH:mm') : undefined,
        end_shift_restriction: end_shift_restriction ? end_shift_restriction.format('HH:mm') : undefined,
        activate_by,
        use_limit_hour_shift_round,
        exception_short_break_unqualif_break,
        min_interval_qualif: min_interval_qualif,
        min_duration_shift_to_activate: min_duration_shift_to_activate,
        max_duration_shift_to_activate: max_duration_shift_to_activate,
        location: location,
        activate_if_location_match_or_after: activate_if_location_match_or_after ? activate_if_location_match_or_after.format('HH:mm') : undefined,
        activate_if_location_is_befora: activate_if_location_is_befora ? activate_if_location_is_befora.format('HH:mm') : undefined,
    };
    console.log(`Body to edit bonus/deduction with shift duration:`, body);
    return tk.bonus_rules.update(bonus_id, body);
};

const editingBonusAndDeductionPaycode = (
    bonus_id: number,
    name: string,
    description: string,
    amount: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean,
    is_exception: boolean,
    paycode_id: number | string,
    break_id: number | string,
    allow_cancel_in_timesheet: boolean,
    use_shift_restriction: boolean,
    start_shift_restriction: Dayjs | null,
    end_shift_restriction: Dayjs | null,
    activate_by: string,
    active_paycode_id: number | string,
    minimum: string,
    maximum: string,
    time_period: string,
    achieve_requirements_for_shift: boolean,
    shifts_required: number,
    start_day_week: string,
) => {
    const days_in_week = [
        monday ? 1 : undefined,
        tuesday ? 2 : undefined,
        wednesday ? 4 : undefined,
        thursday ? 8 : undefined,
        friday ? 16 : undefined,
        saturday ? 32 : undefined,
        sunday ? 64 : undefined,
    ]
        .map((day) => day)
        .filter((day) => day !== undefined);

    const body = {
        name,
        description,
        amount: amount,
        days_in_week,
        is_exception,
        paycode_id: paycode_id ? paycode_id : undefined,
        break_id: break_id ? break_id : undefined,
        allow_cancel_in_timesheet,
        use_shift_restriction,
        start_shift_restriction: start_shift_restriction ? start_shift_restriction.format('HH:mm') : undefined,
        end_shift_restriction: end_shift_restriction ? end_shift_restriction.format('HH:mm') : undefined,
        activate_by,
        active_paycode_id: active_paycode_id,
        minimum: minimum,
        maximum: maximum,
        time_period,
        achieve_requirements_for_shift: achieve_requirements_for_shift,
        shifts_required: shifts_required,
        start_day_week: start_day_week,
    };
    console.log(`Body to edit bonus/deduction with paycode:`, body);
    return tk.bonus_rules.update(bonus_id, body);
};

const deletingBonusAndDeduction = async (zone: number) => {
    return await tk.bonus_rules.delete(zone);
};

export {
    gettingBonusAndDeduction,
    gettingBonusAndDeductionById,
    addingBonusAndDeductionHourDay,
    addingBonusAndDeductionShiftDuration,
    addingBonusAndDeductionPaycode,
    editingBonusAndDeductionHourDay,
    editingBonusAndDeductionShiftDuration,
    editingBonusAndDeductionPaycode,
    deletingBonusAndDeduction,
};
